import { useEffect, useMemo, useState } from 'react';
import Modal from '../components/Modal.jsx';
import DurationInput from '../components/DurationInput.jsx';
import { apiAffaires, apiLoggedTime, apiPhases } from '../api/client.js';
import { fullDayLabel, parseIso, safeColor } from './dateUtils.js';
import MobileTaskField, { NO_TASK, taskPayload } from './MobileTaskField.jsx';
import { lastAffaireIn, rememberLastAffaire } from '../lib/lastAffaire.js';

// Résultats affichés sous la recherche : au-delà, on invite à préciser.
const MAX_RESULTS = 30;

/**
 * « Mes imputations » — ajout d'une imputation sur une affaire HORS planning
 * (équivalent mobile du bouton « Ajouter une affaire » de la vue Tableau).
 *
 * Affaires proposées : périmètre `mineOnly` (membre d'équipe, chef de projet
 * ou saisie passée — imposé côté serveur en app native), sans les affaires
 * « planification uniquement » dont les imputations sont fermées. Le serveur
 * reste l'autorité (`canImputeOnAffaire`, verrou comptable 423, IDOR).
 *
 * Tâche partagée (#76) : choisie ou créée à la volée (`taskName`, résolue
 * côté serveur par `resolveTaskId`).
 */
export default function MobileLogtimeAddModal({ open, date, personId, onClose, onSaved }) {
  const [affaires, setAffaires] = useState([]);
  const [affairesError, setAffairesError] = useState(null);
  const [loadingAffaires, setLoadingAffaires] = useState(false);
  const [query, setQuery] = useState('');
  const [affaireId, setAffaireId] = useState('');
  const [phases, setPhases] = useState([]);
  const [phaseId, setPhaseId] = useState('');
  const [hours, setHours] = useState(null);
  const [notes, setNotes] = useState('');
  const [task, setTask] = useState(NO_TASK);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setAffaireId('');
    setPhaseId('');
    setHours(null);
    setNotes('');
    setTask(NO_TASK);
    setSubmitting(false);
    setError(null);
  }, [open, date]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoadingAffaires(true);
    setAffairesError(null);
    apiAffaires
      .list('active', null, { mineOnly: true })
      .then((data) => {
        if (cancelled) return;
        const list = (data?.items ?? []).filter((a) => !a.planningOnly);
        setAffaires(list);
        // synotis-support#8 — reprend la dernière affaire saisie.
        const last = lastAffaireIn(list);
        if (last) setAffaireId((prev) => prev || last);
      })
      .catch((e) => {
        if (!cancelled) setAffairesError(e.message || 'Affaires indisponibles.');
      })
      .finally(() => {
        if (!cancelled) setLoadingAffaires(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !affaireId) {
      setPhases([]);
      return undefined;
    }
    let cancelled = false;
    apiPhases
      .list(affaireId, 'active')
      .then((data) => {
        if (!cancelled) setPhases(data?.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setPhases([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, affaireId]);

  // Filtre insensible à la casse et aux accents sur code, nom et client. La
  // valeur choisie reste proposée même si elle ne correspond plus au filtre.
  const options = useMemo(() => {
    const norm = (s) =>
      String(s ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();
    const q = norm(query.trim());
    const sorted = [...affaires].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
    if (!q) return sorted;
    return sorted.filter(
      (a) =>
        a.id === affaireId ||
        norm(`${a.code ?? ''} ${a.name} ${a.client?.name ?? ''}`).includes(q),
    );
  }, [affaires, query, affaireId]);

  const selected = affaires.find((a) => a.id === affaireId) ?? null;
  // Les heures ne sont connues qu'au commit du champ (perte de focus) : on ne
  // désactive pas le bouton sur leur valeur, on la contrôle à l'envoi.
  const canSubmit = Boolean(affaireId) && !submitting;

  const submit = async () => {
    if (!canSubmit || !personId) return;
    if (hours == null || hours <= 0) {
      setError('Indiquez un nombre d’heures.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiLoggedTime.create({
        personId,
        affaireId,
        phaseId: phaseId || null,
        allocationId: null,
        date,
        hours,
        notes: notes.trim() || null,
        ...(task.taskId || task.taskName ? taskPayload(task) : {}),
        // Même défaut que la vue Tableau ; le serveur force `false` sur une
        // affaire interne.
        billable: !selected?.nonBillable,
        source: 'manual',
      });
      rememberLastAffaire(affaireId);
      onSaved(date);
    } catch (e) {
      if (e?.status === 423) setError('Période verrouillée : saisie impossible à cette date.');
      else setError(e?.message || 'Échec de l’enregistrement.');
      setSubmitting(false);
    }
  };

  const close = submitting ? () => {} : onClose;

  return (
    <Modal
      open={open}
      onClose={close}
      title="Ajouter une affaire"
      subtitle={date ? fullDayLabel(parseIso(date)) : undefined}
      size="sm"
      footer={
        <div className="m-modal-footer">
          <span className="m-modal-footer__spacer" />
          <button type="button" className="m-btn" onClick={close} disabled={submitting}>
            Annuler
          </button>
          <button
            type="button"
            className="m-btn m-btn--primary"
            onClick={submit}
            disabled={!canSubmit}
          >
            {submitting ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      }
    >
      <div className="m-form">
        {/* Recherche au clavier : un <select> natif ouvre sur iPhone une roue
            qu'on ne peut pas filtrer. Saisir filtre la liste, toucher choisit. */}
        <div className="m-field">
          <span className="m-field__label">Affaire</span>
          {selected ? (
            <div className="m-pick__selected">
              <span className="m-block__dot" style={{ background: safeColor(selected.color) }} />
              <span className="m-pick__label">
                {selected.code ? `${selected.code} — ${selected.name}` : selected.name}
              </span>
              <button
                type="button"
                className="m-btn"
                onClick={() => {
                  setAffaireId('');
                  setPhaseId('');
                  setTask(NO_TASK);
                }}
              >
                Changer
              </button>
            </div>
          ) : (
            <>
              <input
                className="m-input"
                type="search"
                value={query}
                autoFocus
                autoComplete="off"
                enterKeyHint="search"
                placeholder="Tapez un code, un nom ou un client"
                aria-label="Rechercher une affaire"
                onChange={(e) => setQuery(e.target.value)}
              />
              <div className="m-pick" role="listbox" aria-label="Affaires">
                {loadingAffaires ? (
                  <div className="m-pick__empty">Chargement…</div>
                ) : options.length === 0 ? (
                  query ? <div className="m-pick__empty">Aucune affaire ne correspond.</div> : null
                ) : (
                  options.slice(0, MAX_RESULTS).map((a) => (
                    <button
                      type="button"
                      role="option"
                      aria-selected="false"
                      key={a.id}
                      className="m-pick__item"
                      onClick={() => {
                        setAffaireId(a.id);
                        setPhaseId('');
                        setError(null);
                      }}
                    >
                      <span className="m-block__dot" style={{ background: safeColor(a.color) }} />
                      <span className="m-pick__label">
                        <span className="m-pick__name">
                          {a.code ? `${a.code} — ${a.name}` : a.name}
                        </span>
                        {a.client?.name ? <span className="m-pick__sub">{a.client.name}</span> : null}
                      </span>
                    </button>
                  ))
                )}
                {options.length > MAX_RESULTS ? (
                  <div className="m-pick__empty">
                    {options.length - MAX_RESULTS} autre(s) : précisez la recherche.
                  </div>
                ) : null}
              </div>
            </>
          )}
          {affairesError ? <span className="m-field__error">{affairesError}</span> : null}
          {!loadingAffaires && !affairesError && affaires.length === 0 ? (
            <span className="m-field__hint">
              Aucune affaire disponible : vous devez faire partie de l’équipe d’une affaire.
            </span>
          ) : null}
        </div>

        {phases.length > 0 ? (
          <label className="m-field">
            <span className="m-field__label">Phase</span>
            <select className="m-input" value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
              <option value="">— Sans phase —</option>
              {phases.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <MobileTaskField
          affaireId={affaireId}
          value={task}
          onChange={setTask}
          disabled={submitting}
        />

        <div className="m-field">
          <span className="m-field__label">Heures</span>
          <DurationInput
            value={hours}
            placeholder="0:00"
            ariaLabel="Heures"
            style={{ fontSize: 16, width: 96 }}
            onChange={(h) => setHours(h)}
          />
        </div>

        <label className="m-field">
          <span className="m-field__label">Note</span>
          <textarea
            className="m-input"
            rows={2}
            placeholder="Facultatif"
            maxLength={5000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        {error ? <span className="m-field__error">{error}</span> : null}
      </div>
    </Modal>
  );
}
